
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import PlayIcon from './icons/PlayIcon';
import PauseIcon from './icons/PauseIcon';
import Volume2Icon from './icons/Volume2Icon';
import VolumeXIcon from './icons/VolumeXIcon';
import MaximizeIcon from './icons/MaximizeIcon';
import XIcon from './icons/XIcon';

interface VideoPlayerProps {
    src: string;
    poster: string;
    onClose?: () => void;
}

const formatTime = (timeInSeconds: number) => {
    if (isNaN(timeInSeconds)) return '00:00';
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

const VideoPlayer: React.FC<VideoPlayerProps> = ({ src, poster, onClose }) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const progressRef = useRef<HTMLInputElement>(null);

    const controlsTimeoutRef = useRef<number | null>(null);

    const [isPlaying, setIsPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(1);
    const [isMuted, setIsMuted] = useState(false);
    const [isControlsVisible, setIsControlsVisible] = useState(true);

    const togglePlay = useCallback(() => {
        if (!videoRef.current) return;
        if (videoRef.current.paused) {
            videoRef.current.play();
            setIsPlaying(true);
        } else {
            videoRef.current.pause();
            setIsPlaying(false);
        }
    }, []);

    const handleTimeUpdate = () => {
        if (videoRef.current) {
            setProgress(videoRef.current.currentTime);
        }
    };

    const handleLoadedMetadata = () => {
        if (videoRef.current) {
            setDuration(videoRef.current.duration);
        }
    };

    const handleProgressSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!videoRef.current) return;
        const time = Number(e.target.value);
        videoRef.current.currentTime = time;
        setProgress(time);
    };

    const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!videoRef.current) return;
        const newVolume = Number(e.target.value);
        videoRef.current.volume = newVolume;
        setVolume(newVolume);
        if (newVolume > 0) {
            setIsMuted(false);
            videoRef.current.muted = false;
        } else {
            setIsMuted(true);
            videoRef.current.muted = true;
        }
    };

    const toggleMute = () => {
        if (!videoRef.current) return;
        videoRef.current.muted = !isMuted;
        setIsMuted(!isMuted);
    };

    const toggleFullScreen = () => {
        if (containerRef.current) {
            if (!document.fullscreenElement) {
                containerRef.current.requestFullscreen().catch(err => {
                    alert(`Error attempting to enable full-screen mode: ${err.message} (${err.name})`);
                });
            } else {
                document.exitFullscreen();
            }
        }
    };

    const hideControls = () => {
        if (isPlaying) {
            setIsControlsVisible(false);
        }
    };

    const showControls = () => {
        setIsControlsVisible(true);
        if (controlsTimeoutRef.current) {
            clearTimeout(controlsTimeoutRef.current);
        }
        controlsTimeoutRef.current = window.setTimeout(hideControls, 3000);
    };

    useEffect(() => {
        const video = videoRef.current;
        if (video) {
            video.addEventListener('timeupdate', handleTimeUpdate);
            video.addEventListener('loadedmetadata', handleLoadedMetadata);
            video.addEventListener('ended', () => {
                setIsPlaying(false);
                if (videoRef.current) videoRef.current.currentTime = 0;
            });
        }
        return () => {
            if (video) {
                video.removeEventListener('timeupdate', handleTimeUpdate);
                video.removeEventListener('loadedmetadata', handleLoadedMetadata);
                video.removeEventListener('ended', () => setIsPlaying(false));
            }
            if (controlsTimeoutRef.current) {
                clearTimeout(controlsTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (isPlaying) {
            showControls();
        } else {
            setIsControlsVisible(true);
            if (controlsTimeoutRef.current) {
                clearTimeout(controlsTimeoutRef.current);
            }
        }
    }, [isPlaying]);

    return (
        <div
            ref={containerRef}
            className="relative w-full h-full bg-black rounded-2xl overflow-hidden group"
            onMouseMove={showControls}
            onMouseLeave={hideControls}
            onClick={e => { e.stopPropagation(); togglePlay(); }}
        >
            <video
                ref={videoRef}
                src={src}
                poster={poster}
                className="w-full h-full object-contain pointer-events-none"
                playsInline
            />

            <AnimatePresence>
                {isControlsVisible && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="absolute inset-0 bg-black/20 pointer-events-none"
                    />
                )}
            </AnimatePresence>

            {/* Close Button */}
            <AnimatePresence>
                {onClose && isControlsVisible && (
                    <motion.button
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        onClick={(e) => { e.stopPropagation(); onClose(); }}
                        className="absolute top-4 right-4 z-50 p-2 rounded-full bg-black/60 backdrop-blur-md text-white hover:bg-black/80 transition-colors border border-white/20 shadow-lg"
                        title="Close Video"
                    >
                        <XIcon className="w-5 h-5" />
                    </motion.button>
                )}
            </AnimatePresence>

            <AnimatePresence>
                {!isPlaying && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                        className="absolute inset-0 flex items-center justify-center pointer-events-none"
                    >
                        <motion.div
                            animate={{ scale: [1, 1.05, 1] }}
                            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                            className="w-20 h-20 rounded-full bg-black/40 backdrop-blur-md flex items-center justify-center border border-white/20 shadow-[0_0_20px_rgba(20,184,166,0.5)]"
                        >
                            <PlayIcon className="w-10 h-10 text-white ml-1" />
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            <AnimatePresence>
                {isControlsVisible && (
                    <motion.div
                        initial={{ y: '100%' }}
                        animate={{ y: '0%' }}
                        exit={{ y: '100%' }}
                        transition={{ duration: 0.3, ease: 'easeInOut' }}
                        className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/70 to-transparent pointer-events-auto"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="flex flex-col gap-2">
                            <div className="relative w-full">
                                <input
                                    ref={progressRef}
                                    type="range"
                                    min="0"
                                    max={duration}
                                    value={progress}
                                    onChange={handleProgressSeek}
                                    className="w-full h-1.5 bg-white/20 rounded-full appearance-none cursor-pointer transition-all duration-200 hover:h-2 video-progress-bar"
                                    style={{
                                        '--progress-percent': `${(progress / duration) * 100}%`
                                    } as React.CSSProperties}
                                />
                            </div>

                            <div className="flex items-center justify-between text-white text-xs font-medium">
                                <div className="flex items-center gap-3">
                                    <button onClick={togglePlay} className="p-1.5 rounded-full hover:bg-white/20 transition-colors">
                                        {isPlaying ? <PauseIcon className="w-5 h-5" /> : <PlayIcon className="w-5 h-5" />}
                                    </button>
                                    <div className="flex items-center gap-2 group/volume">
                                        <button onClick={toggleMute} className="p-1.5 rounded-full hover:bg-white/20 transition-colors">
                                            {isMuted || volume === 0 ? <VolumeXIcon className="w-5 h-5" /> : <Volume2Icon className="w-5 h-5" />}
                                        </button>
                                        <input
                                            type="range"
                                            min="0"
                                            max="1"
                                            step="0.05"
                                            value={isMuted ? 0 : volume}
                                            onChange={handleVolumeChange}
                                            className="w-0 group-hover/volume:w-20 transition-all duration-300 h-1 bg-white/20 rounded-full appearance-none cursor-pointer video-volume-slider"
                                            style={{ '--volume-percent': `${(isMuted ? 0 : volume) * 100}%` } as React.CSSProperties}
                                        />
                                    </div>
                                    <span className="tabular-nums">{formatTime(progress)} / {formatTime(duration)}</span>
                                </div>
                                <button onClick={toggleFullScreen} className="p-1.5 rounded-full hover:bg-white/20 transition-colors">
                                    <MaximizeIcon className="w-5 h-5" />
                                </button>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
            <style>{`
                .video-progress-bar::-webkit-slider-runnable-track {
                    background: linear-gradient(to right, #14b8a6 var(--progress-percent, 0%), rgba(255,255,255,0.2) var(--progress-percent, 0%));
                }
                .video-progress-bar::-moz-range-track {
                    background: linear-gradient(to right, #14b8a6 var(--progress-percent, 0%), rgba(255,255,255,0.2) var(--progress-percent, 0%));
                }
                .video-progress-bar::-webkit-slider-thumb {
                    -webkit-appearance: none; appearance: none;
                    width: 14px; height: 14px; border-radius: 50%; background: #fff; transform: scale(0); transition: transform 0.15s ease-in-out; cursor: pointer;
                }
                .video-progress-bar:hover::-webkit-slider-thumb {
                    transform: scale(1);
                }
                .video-volume-slider::-webkit-slider-runnable-track {
                    background: linear-gradient(to right, #fff var(--volume-percent, 0%), rgba(255,255,255,0.2) var(--volume-percent, 0%));
                }
                .video-volume-slider::-moz-range-track {
                     background: linear-gradient(to right, #fff var(--volume-percent, 0%), rgba(255,255,255,0.2) var(--volume-percent, 0%));
                }
                 .video-volume-slider::-webkit-slider-thumb {
                    -webkit-appearance: none; appearance: none;
                    width: 12px; height: 12px; border-radius: 50%; background: #fff; cursor: pointer;
                }
            `}</style>
        </div>
    );
};

export default VideoPlayer;
